import React from "react";

const LobbyPanel = ({ state, actions, helpers }) => {
  const {
    lobbyLabel,
    lobbyLabelPlural,
    selectedRound,
    selectedLobby,
    selectedMatch,
    roomId,
    roomPassword,
    sending,
    lobbyEntries,
    selectedEntryId,
    availableEntries,
    matches,
    matchForm,
    selectedMatchId,
    battleResults,
    format,
    isBracketFormat,
    activeBestOf,
    activeBestOfWins,
    lobbySlotEntries,
    bracketResult,
    entryTypeLabel,
    saving,
  } = state;
  const {
    setActivePanel,
    setRoomId,
    setRoomPassword,
    saveLobbyCredentials,
    sendLobbyCredentials,
    setSelectedEntryId,
    handleAddEntry,
    handleRemoveEntry,
    setMatchForm,
    createMatch,
    setSelectedMatchId,
    handleDeleteMatch,
    updateBattleResult,
    updatePlayerKills,
    setBracketResult,
    saveResults,
  } = actions;
  const { formatBestOfLabel } = helpers;

  return (
    <>
      <div className="manage-toolbar">
        <button
          className="button ghost"
          type="button"
          onClick={() => setActivePanel("operations")}
        >
          Back to rounds & {lobbyLabelPlural.toLowerCase()}
        </button>
        <div className="panel-meta">
          <span className="mini-pill">
            {selectedRound?.roundName || "Round"}
          </span>
          <span className="mini-pill">
            {selectedLobby?.lobbyName || lobbyLabel}
          </span>
          <span className="mini-pill">
            {selectedMatch?.matchName || "Match"}
          </span>
        </div>
      </div>

      <div className="manage-grid">
        <div className="manage-column">
          <div className="card">
            <div className="card-header">
              <h3>{lobbyLabel} credentials</h3>
            </div>
            {!selectedLobby ? (
              <div className="empty-inline">
                Select a {lobbyLabel.toLowerCase()} to manage.
              </div>
            ) : (
              <>
                <div className="form-grid">
                  <label className="field">
                    <span>Room ID</span>
                    <input
                      value={roomId}
                      onChange={(event) => setRoomId(event.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Room password</span>
                    <input
                      value={roomPassword}
                      onChange={(event) => setRoomPassword(event.target.value)}
                    />
                  </label>
                </div>
                <div className="inline-actions">
                  <button
                    className="button ghost"
                    type="button"
                    onClick={saveLobbyCredentials}
                  >
                    Save credentials
                  </button>
                  <button
                    className="button primary"
                    type="button"
                    onClick={sendLobbyCredentials}
                  >
                    {sending ? "Sending..." : "Send ID/Pass"}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3>Assigned {entryTypeLabel}</h3>
              <span className="chip">{lobbyEntries.length} assigned</span>
            </div>
            {!selectedLobby ? (
              <div className="empty-inline">
                Select a {lobbyLabel.toLowerCase()} to manage entries.
              </div>
            ) : lobbyEntries.length === 0 ? (
              <div className="empty-inline">No entries assigned yet.</div>
            ) : (
              <div className="list">
                {lobbyEntries.map((entry) => (
                  <div className="list-row" key={entry.id}>
                    <div>
                      <div className="list-title">{entry.name}</div>
                      <div className="list-subtitle">
                        {entry.type === "team"
                          ? `${
                              entry.members?.length ||
                              entry.memberIds?.length ||
                              0
                            } players`
                          : "Solo player"}
                      </div>
                    </div>
                    <button
                      className="button danger"
                      type="button"
                      onClick={() => handleRemoveEntry(entry.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!!selectedLobby && (
              <div className="form-section">
                <div className="section-title">Add entry</div>
                <select
                  value={selectedEntryId}
                  onChange={(event) => setSelectedEntryId(event.target.value)}
                >
                  <option value="">Select from registrations</option>
                  {availableEntries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
                <button className="button primary" type="button" onClick={handleAddEntry}>
                  Add to {lobbyLabel.toLowerCase()}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="manage-column">
          <div className="card">
            <div className="card-header">
              <h3>Matches</h3>
              <span className="chip">{matches.length} total</span>
            </div>
            {!selectedLobby ? (
              <div className="empty-inline">
                Select a {lobbyLabel.toLowerCase()} to manage matches.
              </div>
            ) : (
              <>
                <div className="form-grid">
                  <label className="field">
                    <span>Match name</span>
                    <input
                      value={matchForm.name}
                      onChange={(event) =>
                        setMatchForm((prev) => ({
                          ...prev,
                          name: event.target.value,
                        }))
                      }
                      placeholder="Match 1"
                    />
                  </label>
                  <label className="field">
                    <span>Match number</span>
                    <input
                      type="number"
                      value={matchForm.number}
                      onChange={(event) =>
                        setMatchForm((prev) => ({
                          ...prev,
                          number: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <button className="button primary" type="button" onClick={createMatch}>
                  Add match
                </button>
                <div className="divider" />
                {matches.length === 0 ? (
                  <div className="empty-inline">No matches yet.</div>
                ) : (
                  <div className="list">
                    {matches.map((match) => (
                      <div
                        key={match.id}
                        className={`list-row ${
                          match.id === selectedMatchId ? "active-row" : ""
                        }`}
                        onClick={() => setSelectedMatchId(match.id)}
                        role="button"
                        tabIndex={0}
                      >
                        <div>
                          <div className="list-title">{match.matchName}</div>
                          <div className="list-subtitle">Match {match.matchNumber}</div>
                        </div>
                        <div className="list-actions">
                          <div className="list-meta">
                            {match.resultsUpdatedAt ? "Results saved" : "Pending"}
                          </div>
                          <button
                            className="button danger small"
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteMatch(match.id);
                            }}
                            disabled={saving}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <h3>Match results</h3>
                <div className="helper-text">
                  {isBracketFormat
                    ? `Best of ${activeBestOf} · first to ${activeBestOfWins} wins.`
                    : "Save results for the selected match."}
                </div>
              </div>
              <div className="panel-meta">
                <span className="mini-pill">
                  {selectedMatch?.matchName || "Match"}
                </span>
                {isBracketFormat && activeBestOf ? (
                  <span className="mini-pill">
                    {formatBestOfLabel(activeBestOf)}
                  </span>
                ) : null}
              </div>
            </div>
            {!selectedLobby ? (
              <div className="empty-inline">
                Select a {lobbyLabel.toLowerCase()} to enter results.
              </div>
            ) : !selectedMatch ? (
              <div className="empty-inline">
                Create or select a match to enter results.
              </div>
            ) : format === "battle_royale" ? (
              lobbyEntries.length === 0 ? (
                <div className="empty-inline">Assign teams to enter results.</div>
              ) : (
                <div className="results-grid">
                  {battleResults.map((result) => (
                    <div className="result-card" key={result.id}>
                      <div className="result-header">
                        <div>
                          <div className="result-title">{result.name}</div>
                          <div className="result-subtitle">
                            {result.playerKills?.length || 0} players
                          </div>
                        </div>
                        <div className="result-total">
                          <div className="result-total-value">
                            {result.totalPoints}
                          </div>
                          <div className="result-total-label">Total</div>
                        </div>
                      </div>

                      <div className="result-fields">
                        <label className="field">
                          <span>Placement</span>
                          <input
                            type="number"
                            value={result.placement}
                            onChange={(event) =>
                              updateBattleResult(
                                result.id,
                                "placement",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label className="field">
                          <span>Team kills</span>
                          <input type="number" value={result.kills} readOnly />
                        </label>
                        <label className="field">
                          <span>Position points</span>
                          <input type="number" value={result.positionPoints} readOnly />
                        </label>
                        <label className="field">
                          <span>Total points</span>
                          <input type="number" value={result.totalPoints} readOnly />
                        </label>
                        <label className="field">
                          <span>WWCD</span>
                          <input
                            type="checkbox"
                            checked={Boolean(result.wwcd)}
                            onChange={(event) =>
                              updateBattleResult(
                                result.id,
                                "wwcd",
                                event.target.checked
                              )
                            }
                          />
                        </label>
                      </div>

                      <div className="player-list">
                        <div className="section-title">Player kills</div>
                        {Array.isArray(result.playerKills) &&
                        result.playerKills.length > 0 ? (
                          result.playerKills.map((player) => (
                            <div className="player-row" key={player.id}>
                              <div className="player-name">
                                <div className="player-primary">
                                  {player.inGameName ||
                                    player.name ||
                                    "Player"}
                                </div>
                                {player.inGameId ? (
                                  <div className="player-secondary">
                                    ID: {player.inGameId}
                                  </div>
                                ) : null}
                              </div>
                              <input
                                type="number"
                                value={player.kills}
                                onChange={(event) =>
                                  updatePlayerKills(
                                    result.id,
                                    player.id,
                                    event.target.value
                                  )
                                }
                              />
                            </div>
                          ))
                        ) : (
                          <div className="empty-inline">
                            No players listed for this team.
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="form-section">
                {lobbyEntries.length === 0 ? (
                  <div className="empty-inline">
                    Assign two entries to enter results.
                  </div>
                ) : (
                  <>
                    <div className="list">
                      {lobbySlotEntries.map((entry, index) => (
                        <div className="list-row" key={entry.id}>
                          <div>
                            <div className="list-title">{entry.name}</div>
                            <div className="list-subtitle">
                              {entry.type === "player" ? "Player" : "Team"}
                            </div>
                          </div>
                          <input
                            type="number"
                            value={
                              index === 0
                                ? bracketResult.scoreA
                                : bracketResult.scoreB
                            }
                            onChange={(event) =>
                              setBracketResult((prev) => ({
                                ...prev,
                                [index === 0 ? "scoreA" : "scoreB"]:
                                  event.target.value,
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                    <label className="field">
                      <span>Winner</span>
                      <select
                        value={bracketResult.winnerId}
                        onChange={(event) =>
                          setBracketResult((prev) => ({
                            ...prev,
                            winnerId: event.target.value,
                          }))
                        }
                      >
                        <option value="">Select winner</option>
                        {lobbySlotEntries.map((entry) => (
                          <option key={entry.id} value={entry.id}>
                            {entry.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
              </div>
            )}

            {selectedMatch && (
              <button className="button primary" type="button" onClick={saveResults}>
                Save results
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LobbyPanel;
